// Semantic operator-gating policy shared by the IntellisenseEngine completion
// path and (implicitly) VQE's curated operator lattices.
//
// The keyword/built-in completion path over-surfaces standard comparison
// operators: after `WHERE col ▮` it offers `LIKE`, `BETWEEN`, `<`, `>`, … for
// EVERY column regardless of type. VQE's closed dropdown never had this
// problem — it routes column operands through `suggestOperatorsForOperand`,
// whose lattices already exclude type-inappropriate operators (NUMERIC has no
// `LIKE`; BOOLEAN is equality + null only). This module closes that asymmetry
// for IntelliSense by declaring which standard operators are *semantically
// invalid* for a column of a given SQL type, so the resolver can prune them.
//
// Why a separate policy instead of reusing the lattice membership directly:
// the VQE lattices are a *curated* closed list (TEXT_OPERATORS deliberately
// omits `<` / `>` / `BETWEEN` to keep the dropdown short), but those operators
// are still valid SQL on text columns (`name > 'M'`, `name BETWEEN 'A' AND
// 'M'`). For a free-form completion list we only want to suppress operators
// that are genuinely wrong for the type — not merely absent from VQE's
// curation. Hence an explicit invalid-set keyed on the type, kept narrow.

import type { OperandLiteralSqlType } from '../../types';

const NUMERIC_INVALID: ReadonlySet<string> = new Set(['LIKE', 'NOT LIKE']);

// Booleans support only equality + null checks; pattern-matching, ranging and
// ordering a boolean are all meaningless.
const BOOLEAN_INVALID: ReadonlySet<string> = new Set([
  'LIKE',
  'NOT LIKE',
  'BETWEEN',
  '<',
  '>',
  '<=',
  '>=',
]);

const EMPTY: ReadonlySet<string> = new Set();

/**
 * Standard operator tokens that are semantically invalid for a column of the
 * given SQL type. Returned tokens are uppercase (`LIKE`, `BETWEEN`) or the
 * literal symbol (`<`); callers compare against an upper-cased candidate
 * label. `text`, `null` and `unknown` suppress nothing — text supports the
 * full comparison/pattern set, and without a confident type we stay
 * conservative (no suppression).
 */
export function invalidStandardOperatorsForColumnType(
  columnSqlType: OperandLiteralSqlType,
): ReadonlySet<string> {
  switch (columnSqlType) {
    case 'integer':
    case 'numeric':
      return NUMERIC_INVALID;
    case 'boolean':
      return BOOLEAN_INVALID;
    case 'text':
    case 'null':
    case 'unknown':
    default:
      return EMPTY;
  }
}
